import { useNavigate } from 'react-router-dom';

import { BRANDING } from '@perplexicon/config';
import type {
  CurrentUser,
  GameCatalogEntry,
  Invitation,
  SessionSummary,
} from '@perplexicon/api-client';

import { assetUrl } from '../../assets/asset-manifest.js';
import {
  ActiveGameCard,
  ActiveGameConflictDialog,
  ActionLink,
  Avatar,
  Button,
  Card,
  ErrorState,
  GameCard,
  LoadingState,
  PageLayout,
  SectionHeading,
} from '../../components/index.js';
import { isActiveGameConflict, useActiveGameConflict } from '../../lib/active-game-conflict.js';
import { useCurrentUser } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';
import { formatDate } from '../../lib/format-date.js';
import { activeSessionsByGame } from '../../lib/game-meta.js';
import { useActiveSessions, useCompletedSessions, useGameCatalog } from '../../lib/games.js';
import { useStartGameFlow } from '../../lib/start-game-flow.js';
import {
  useAcceptInvitation,
  useDeclineInvitation,
  useIncomingInvitations,
} from '../../lib/invitations.js';
import { opponentNames } from '../../lib/session-display.js';
import { resultHeadline } from '../../lib/session-outcome.js';
import { CreateLobbyDialog } from '../games/CreateLobbyDialog.js';
import { CreateMultiplayerDialog } from '../games/CreateMultiplayerDialog.js';

/** How many finished games the dashboard summarizes before deferring to Game History. */
const RECENT_LIMIT = 4;

function resultLabel(entry: SessionSummary, selfId: string): string {
  const own = entry.players.find((p) => p.userId === selfId);
  return own?.result ? resultHeadline(own.result) : entry.summary.headline;
}

/**
 * "Continue playing" (APP_SPEC §18) — every session waiting on the player,
 * rendered with the same `ActiveGameCard` the Games page's own "Continue
 * Playing" section uses. No cap or "view all" link: a player holds at most
 * one live session per game per slot (`active-session-limit.ts`), so the
 * list is bounded by the size of the catalog itself.
 */
function ContinuePlayingSection({ selfId }: { selfId: string }) {
  const activeSessions = useActiveSessions();

  if (activeSessions.isPending) return <LoadingState label="Loading active games…" />;
  if (activeSessions.isError) {
    return (
      <ErrorState
        message={getErrorMessage(activeSessions.error)}
        onRetry={() => void activeSessions.refetch()}
      />
    );
  }
  if (activeSessions.data.length === 0) {
    // One quiet line, not a full empty-state panel: an empty section should
    // never out-weigh the games above it (docs/UI_GUIDELINES.md §8).
    return (
      <p className="text-sm text-textSecondary">
        Nothing in progress. Choose a game above to begin.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {activeSessions.data.map((entry) => (
        <li key={entry.id}>
          <ActiveGameCard entry={entry} selfId={selfId} />
        </li>
      ))}
    </ul>
  );
}

/**
 * "Recent adventures" — a short, scannable summary of finished games, not a
 * second copy of Game History (APP_SPEC §44). Rows carry only game,
 * opponent, result and date; the full record stays one link away.
 */
function RecentAdventuresSection({ selfId }: { selfId: string }) {
  const completedSessions = useCompletedSessions();

  if (completedSessions.isPending) return <LoadingState label="Loading recent games…" />;
  if (completedSessions.isError) {
    return (
      <ErrorState
        message={getErrorMessage(completedSessions.error)}
        onRetry={() => void completedSessions.refetch()}
      />
    );
  }
  if (completedSessions.data.length === 0) {
    return <p className="text-sm text-textSecondary">No finished games yet.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <Card className="p-0">
        <ul className="flex flex-col">
          {completedSessions.data.slice(0, RECENT_LIMIT).map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-divider px-3 py-2 last:border-b-0"
            >
              <span className="min-w-0 truncate font-medium text-textPrimary">
                {entry.gameName}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-textSecondary">
                vs {opponentNames(entry, selfId)}
              </span>
              <span className="shrink-0 text-sm font-semibold text-textPrimary">
                {resultLabel(entry, selfId)}
              </span>
              <span className="shrink-0 text-xs text-textMuted">
                {entry.completedAt ? formatDate(entry.completedAt) : ''}
              </span>
            </li>
          ))}
        </ul>
      </Card>
      {/* Game History lives under Settings (docs/UI_GUIDELINES.md §5). */}
      <ActionLink to="/settings?tab=history" showArrow className="self-start">
        View game history
      </ActionLink>
    </div>
  );
}

/**
 * Incoming game invitations (APP_SPEC §20), answerable in place. Accepting
 * routes through the shared one-active-session conflict handler exactly as
 * the notifications screen does, so the two behave identically.
 *
 * The list is passed in rather than fetched here: the caller already knows
 * whether there is anything pending, because with none it removes the whole
 * section rather than dedicating a heading and a panel to "none".
 */
function InvitationsSection({
  invitations,
  gameNames,
  onConflict,
}: {
  invitations: Invitation[];
  gameNames: Map<string, string>;
  onConflict: (error: unknown, retry: () => void) => boolean;
}) {
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();
  const navigate = useNavigate();

  function joinGame(invitationId: string) {
    accept.mutate(invitationId, {
      onSuccess: (session) => {
        void navigate(`/play/${session.id}`);
      },
      onError: (error) => onConflict(error, () => joinGame(invitationId)),
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {invitations.map((invitation) => {
          const gameName = gameNames.get(invitation.gameId);
          return (
            <li key={invitation.id}>
              <Card className="flex flex-col gap-3 p-3 tablet:flex-row tablet:items-center tablet:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar
                    seed={invitation.otherUser.id}
                    label={invitation.otherUser.username}
                    config={invitation.otherUser.avatar}
                    size="sm"
                  />
                  <p className="min-w-0 text-sm text-textPrimary">
                    <span className="font-semibold">{invitation.otherUser.username}</span>
                    {gameName ? ` invited you to ${gameName}` : ' invited you to play'}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    isLoading={accept.isPending && accept.variables === invitation.id}
                    disabled={decline.isPending}
                    onClick={() => joinGame(invitation.id)}
                  >
                    Accept
                    <span className="sr-only">
                      {' '}
                      invitation from {invitation.otherUser.username}
                    </span>
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={decline.isPending && decline.variables === invitation.id}
                    disabled={accept.isPending}
                    onClick={() => decline.mutate(invitation.id)}
                  >
                    Decline
                    <span className="sr-only">
                      {' '}
                      invitation from {invitation.otherUser.username}
                    </span>
                  </Button>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
      {accept.isError && !isActiveGameConflict(accept.error) && (
        <ErrorState message={getErrorMessage(accept.error)} />
      )}
      {decline.isError && <ErrorState message={getErrorMessage(decline.error)} />}
    </div>
  );
}

/**
 * "Choose a game" — the dashboard's first actionable section, using the same
 * `GameCard` as the catalog (one card, one description, one set of controls)
 * and the same real active-session data, so a game already in progress
 * offers to resume rather than to start again.
 */
function ChooseGameSection({
  catalog,
  activeByGame,
  onConflict,
}: {
  catalog: GameCatalogEntry[];
  activeByGame: Map<string, SessionSummary>;
  onConflict: (error: unknown, retry: () => void) => boolean;
}) {
  const { createSolo, isStarting, startSolo, versusGame, setVersusGame, lobbyGame, setLobbyGame } =
    useStartGameFlow(onConflict);

  const shown = catalog.slice(0, 4);

  return (
    <div className="flex flex-col gap-3">
      {/* Three across from desktop up: these are entry points beside the
          rest of the dashboard, so they stay smaller than the catalog's own
          cards rather than filling half the page each. */}
      <div className="grid gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
        {shown.map((game) => (
          <GameCard
            key={game.gameId}
            game={game}
            headingLevel={3}
            activeSession={activeByGame.get(game.gameId) ?? null}
            isStarting={isStarting(game.gameId)}
            onPlaySolo={() => startSolo(game.gameId)}
            onPlayVersus={() => setVersusGame(game)}
            onHostLobby={() => setLobbyGame(game)}
          />
        ))}
      </div>
      {catalog.length > shown.length && (
        <ActionLink to="/games" showArrow className="self-start">
          View all games
        </ActionLink>
      )}
      {createSolo.isError && !isActiveGameConflict(createSolo.error) && (
        <ErrorState message={getErrorMessage(createSolo.error)} />
      )}
      {versusGame && (
        <CreateMultiplayerDialog
          game={versusGame}
          open={versusGame !== null}
          onOpenChange={(open) => {
            if (!open) setVersusGame(null);
          }}
          onActiveGameConflict={onConflict}
        />
      )}
      {lobbyGame && (
        <CreateLobbyDialog
          game={lobbyGame}
          open={lobbyGame !== null}
          onOpenChange={(open) => {
            if (!open) setLobbyGame(null);
          }}
          onActiveGameConflict={onConflict}
        />
      )}
    </div>
  );
}

/**
 * The player's own greeting (APP_SPEC §18). A small framed area with their
 * avatar and name — enough to feel like their space without becoming a
 * marketing hero (docs/UI_GUIDELINES.md §1 "a few strong visual moments").
 * "Welcome back" is the eyebrow; the page's `h1` is the player's own name.
 */
function DashboardGreeting({ user }: { user: CurrentUser }) {
  return (
    <header className="flex items-center gap-4 rounded-lg border border-border border-l-4 border-l-brand bg-surface p-4 shadow-2 tablet:gap-5 tablet:p-5">
      <Avatar seed={user.id} label={user.username} config={user.avatar} size="lg" loading="eager" />
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-accent">Welcome back</p>
        <h1 className="page-title truncate">{user.username}</h1>
        <p className="mt-1 font-editorial text-sm text-textSecondary tablet:text-base">
          Pick up where you left off, or begin something new.
        </p>
      </div>
      <img
        src={assetUrl('decorRune')}
        alt=""
        aria-hidden="true"
        width={64}
        height={64}
        className="ml-auto hidden h-10 w-10 shrink-0 opacity-70 tablet:block"
      />
    </header>
  );
}

/**
 * Home. Signed out, this is the public landing page; signed in, it is the
 * player's dashboard (APP_SPEC §18, §65 "Platform"; docs/APP_PLAN.md §12 M5b).
 *
 * Order follows what the player can act on: who they are, what they can
 * start, what is already running, what is waiting on an answer, and finally
 * what they have finished. Invitations disappear entirely when there are
 * none. Every section reads from the platform's own session/invitation/
 * catalog data; no game-specific rendering exists here beyond each game's own
 * declared name, description, art, and `getSessionSummary` text (CLAUDE.md §2).
 */
export function DashboardPage() {
  const { data: user } = useCurrentUser();
  const catalog = useGameCatalog();
  const activeSessions = useActiveSessions();
  const incoming = useIncomingInvitations();
  // One conflict handler for every way into a game on this page — starting
  // solo, inviting an opponent, hosting a lobby, accepting an invitation
  // (APP_SPEC §21.1) — so only one resume-or-abandon dialog can ever be open.
  const conflict = useActiveGameConflict();

  if (!user) {
    return (
      // `readable`, not the shell's `wide` default (2026-09-20): this is a
      // single centered marketing card, not a content page, which a wide
      // container would strand rather than "matching" anything.
      <PageLayout width="readable" className="py-8 tablet:py-14">
        <Card
          variant="framed"
          className="flex flex-col items-center gap-4 px-6 py-10 text-center tablet:px-10 tablet:py-14"
        >
          <img
            src={assetUrl('decorFlourish')}
            alt=""
            aria-hidden="true"
            width={64}
            height={64}
            className="h-8 w-8"
          />
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-accent">
            Games for curious minds
          </p>
          <h1 className="brand-type text-4xl text-textPrimary tablet:text-5xl">
            {BRANDING.appName}
          </h1>
          <p className="max-w-md font-editorial text-lg text-textSecondary">
            {BRANDING.description}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <ActionLink to="/register" variant="primary" emphasis="decorated">
              Start playing
            </ActionLink>
            <ActionLink to="/login" variant="secondary">
              Log in
            </ActionLink>
          </div>
        </Card>
      </PageLayout>
    );
  }

  const invitationCount = incoming.data?.length ?? 0;
  const activeByGame = activeSessionsByGame(activeSessions.data ?? []);
  const gameNames = new Map((catalog.data ?? []).map((game) => [game.gameId, game.name]));

  return (
    <PageLayout className="gap-8">
      <DashboardGreeting user={user} />

      <section className="flex flex-col gap-3">
        <SectionHeading>Choose a game</SectionHeading>
        {catalog.isPending && <LoadingState label="Loading games…" />}
        {catalog.isError && (
          <ErrorState
            message={getErrorMessage(catalog.error)}
            onRetry={() => void catalog.refetch()}
          />
        )}
        {catalog.data &&
          (catalog.data.length === 0 ? (
            <p className="text-sm text-textSecondary">No games are available right now.</p>
          ) : (
            <ChooseGameSection
              catalog={catalog.data}
              activeByGame={activeByGame}
              onConflict={conflict.capture}
            />
          ))}
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeading>Continue playing</SectionHeading>
        <ContinuePlayingSection selfId={user.id} />
      </section>

      {/*
        Invitations are the only section waiting on the player rather than
        reporting state — but only when one exists. With none pending the
        heading, the panel and the words "no pending invitations" are all
        removed rather than occupying the page (APP_SPEC §20).
      */}
      {(invitationCount > 0 || incoming.isError) && (
        <section className="flex flex-col gap-3">
          <SectionHeading>
            {incoming.isError ? 'Invitations' : `Invitations · ${String(invitationCount)}`}
          </SectionHeading>
          {incoming.isError ? (
            <ErrorState
              message={getErrorMessage(incoming.error)}
              onRetry={() => void incoming.refetch()}
            />
          ) : (
            <InvitationsSection
              invitations={incoming.data ?? []}
              gameNames={gameNames}
              onConflict={conflict.capture}
            />
          )}
        </section>
      )}

      <section className="flex flex-col gap-3">
        <SectionHeading>Recent adventures</SectionHeading>
        <RecentAdventuresSection selfId={user.id} />
      </section>

      <ActiveGameConflictDialog
        conflict={conflict.conflict}
        onAbandon={conflict.abandonAndRetry}
        onDismiss={conflict.dismiss}
        isAbandoning={conflict.isAbandoning}
        abandonError={conflict.abandonError}
      />
    </PageLayout>
  );
}
